// Arbiter Auth API-level permission strings that gate UI actions across the
// app. Centralized here so every call site imports a named constant instead
// of hand-typing the permission string — a typo becomes an import error
// instead of a silently-always-false check, and renaming a permission is a
// one-file change (see useCapabilities.js for the per-domain hooks built on
// top of these).
export const PERMISSIONS = {
  // auth:* — Access Management (roles/role-assignments admin). Singular
  // "role"/"role-assignment" — matches the real permission strings granted
  // by every role definition (GET /roles); the plural forms these used to
  // be don't exist on any role at all, so every one of these checks was
  // unconditionally false for everyone, regardless of role.
  ROLES_READ: "auth:role:read",
  ROLES_CREATE: "auth:role:create",
  ROLES_UPDATE: "auth:role:update",
  ROLES_DELETE: "auth:role:delete",
  ROLE_ASSIGNMENTS_READ: "auth:role-assignment:read",
  ROLE_ASSIGNMENTS_CREATE: "auth:role-assignment:create",
  ROLE_ASSIGNMENTS_UPDATE: "auth:role-assignment:update",
  ROLE_ASSIGNMENTS_DELETE: "auth:role-assignment:delete",

  // organization:*
  ORGANIZATION_READ: "organization:read",
  ORGANIZATION_ID_UPDATE: "organization:id:update",
  ORGANIZATION_ID_READ: "organization:id:read",
  ORGANIZATION_ID_WILDCARD: "organization:id:*",

  // user:* — membership/org-access actions
  USER_CREATE: "user:create",
  USER_BULK_CREATE: "user:bulk:create",
  USER_BULK_UPDATE: "user:bulk:update",
  USER_ID_UPDATE: "user:id:update",
  USER_COSTCENTER_READ: "user:costcenter:read",

  // user:copilot:* — usage visibility (shared by several "view usage before
  // acting on budget" call sites)
  COPILOT_USAGE_READ: "user:copilot:usage:read",
  COPILOT_USAGE_ID_READ: "user:copilot:usage:id:read",
  COPILOT_MODEL_READ: "user:copilot:model:read",
  COPILOT_ACTIVITY_READ: "user:copilot:activity:read",
  COPILOT_USAGE_COSTCENTER_READ: "user:copilot:usage:costcenter:read",
  COPILOT_USAGE_COSTCENTER_ID_READ: "user:copilot:usage:costcenter:id:read",

  // user:meteredusage:* — billing usage across every GitHub product (GET /users/metered-usage/...).
  // Org-scoped tier, and the separate enterprise-wide tier under user:meteredusage:enterprise:*.
  // Suffixes are the backend's: summary, trend, product, user, user:id, costcenter, organization (enterprise only),
  // movers, quality — each ending in :read.
  METERED_USAGE_SUMMARY_READ: "user:meteredusage:summary:read",
  METERED_USAGE_TREND_READ: "user:meteredusage:trend:read",
  METERED_USAGE_PRODUCTS_READ: "user:meteredusage:product:read",
  METERED_USAGE_USERS_READ: "user:meteredusage:user:read",
  METERED_USAGE_USER_ID_READ: "user:meteredusage:user:id:read",
  METERED_USAGE_COSTCENTERS_READ: "user:meteredusage:costcenter:read",
  METERED_USAGE_MOVERS_READ: "user:meteredusage:movers:read",
  METERED_USAGE_QUALITY_READ: "user:meteredusage:quality:read",
  METERED_USAGE_ENTERPRISE_SUMMARY_READ: "user:meteredusage:enterprise:summary:read",
  METERED_USAGE_ENTERPRISE_TREND_READ: "user:meteredusage:enterprise:trend:read",
  METERED_USAGE_ENTERPRISE_PRODUCTS_READ: "user:meteredusage:enterprise:product:read",
  METERED_USAGE_ENTERPRISE_USERS_READ: "user:meteredusage:enterprise:user:read",
  METERED_USAGE_ENTERPRISE_USER_ID_READ: "user:meteredusage:enterprise:user:id:read",
  METERED_USAGE_ENTERPRISE_COSTCENTERS_READ: "user:meteredusage:enterprise:costcenter:read",
  METERED_USAGE_ENTERPRISE_ORGANIZATIONS_READ: "user:meteredusage:enterprise:organization:read",
  METERED_USAGE_ENTERPRISE_MOVERS_READ: "user:meteredusage:enterprise:movers:read",
  METERED_USAGE_ENTERPRISE_QUALITY_READ: "user:meteredusage:enterprise:quality:read",
  // Metered export: creation differs per tier; listing / polling / downloading share one set for org and enterprise jobs.
  METERED_USAGE_EXPORT_CREATE: "user:meteredusage:export:create",
  METERED_USAGE_ENTERPRISE_EXPORT_CREATE: "user:meteredusage:enterprise:export:create",
  METERED_USAGE_EXPORT_READ: "user:meteredusage:export:read",
  METERED_USAGE_EXPORT_JOB_READ: "user:meteredusage:export:job:read",
  METERED_USAGE_EXPORT_JOB_DOWNLOAD: "user:meteredusage:export:job:download",

  // user:copilot:budget:* — direct-set budget + budget request review
  COPILOT_BUDGET_CREATE: "user:copilot:budget:create",
  COPILOT_BUDGET_READ: "user:copilot:budget:read",
  COPILOT_BUDGET_ID_READ: "user:copilot:budget:id:read",
  COPILOT_BUDGET_ID_UPDATE: "user:copilot:budget:id:update",
  COPILOT_BUDGET_REQUEST_READ: "user:copilot:budget:request:read",
  COPILOT_BUDGET_REQUEST_ID_CREATE: "user:copilot:budget:request:id:create",
  COPILOT_BUDGET_REQUEST_ID_UPDATE: "user:copilot:budget:request:id:update",
  COPILOT_BUDGET_REQUEST_ID_DELETE: "user:copilot:budget:request:id:delete",

  // user:copilot:dashboard/settings — global dashboard config (gear icon)
  COPILOT_SETTINGS_READ: "user:copilot:settings:read",
  COPILOT_DASHBOARD_SETTING_UPDATE: "user:copilot:dashboard:setting:update",

  // user:copilot:export:* — usage/budget export job lifecycle
  COPILOT_EXPORT_READ: "user:copilot:export:read",
  COPILOT_EXPORT_CREATE: "user:copilot:export:create",
  COPILOT_BUDGET_EXPORT_CREATE: "user:copilot:budget:export:create",
  COPILOT_EXPORT_JOB_READ: "user:copilot:export:job:read",
  COPILOT_EXPORT_JOB_DOWNLOAD: "user:copilot:export:job:download",
};
